// src/pages/admin/AdminDashboard.js
// Company admin dashboard: account, user and activity management (not EBIOS analysis).
// Tabbed layout: Overview / Users / Activities / Accounts.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { dashboardService } from '../../services/dashboardServices';
import { TabBar, PeriodSelector } from '../../components/dashboard/admin/shared';
import AdminOverview from '../../components/dashboard/admin/AdminOverview';
import AdminUsers from '../../components/dashboard/admin/AdminUsers';
import AdminActivities from '../../components/dashboard/admin/AdminActivities';
import AdminAccounts from '../../components/dashboard/admin/AdminAccounts';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const tr = (key, opts) => t(`adminDashboard.${key}`, opts);

  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState(7);
  const [raw, setRaw] = useState(null);
  const [previous, setPrevious] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const fetchData = useCallback(async (days) => {
    try {
      const response = await dashboardService.getProviderMetrics();
      if (!response.success) throw new Error('Failed to fetch dashboard metrics');
      setRaw(response.data || {});
      try {
        const hist = await dashboardService.getHistoricalMetrics(days);
        if (hist.success) setPrevious(hist.data.previousPeriod || {});
      } catch {
        setPrevious({});
      }
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      setError(true);
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchData(period).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData(period);
    setIsRefreshing(false);
  };

  // Filter time-bound rows to the selected period; trend arrays come from the
  // backend (7-day coverage today) and are shown as-is with an honest subtitle.
  const view = useMemo(() => {
    if (!raw) return null;
    const cutoff = Date.now() - period * 24 * 60 * 60 * 1000;
    const inPeriod = (a) => {
      if (!a.timestamp) return true;
      const ts = new Date(a.timestamp).getTime();
      return Number.isNaN(ts) || ts >= cutoff;
    };
    const all = raw.recentActivities || [];
    return {
      metrics: {
        totalUsers: raw.totalUsers || 0,
        totalAnalyses: raw.totalAnalyses ?? raw.activeAnalyses ?? 0,
        activeAnalyses: raw.activeAnalyses ?? raw.totalAnalyses ?? 0,
        recentActivitiesCount: all.filter(inPeriod).length,
        subscriptionDaysRemaining: raw.subscriptionDaysRemaining ?? null
      },
      previous: {
        totalUsers: previous.totalUsers || 0,
        totalAnalyses: previous.totalAnalyses ?? previous.activeAnalyses ?? 0,
        activeAnalyses: previous.activeAnalyses ?? previous.totalAnalyses ?? 0,
        recentActivitiesCount: previous.recentActivitiesCount || 0,
        subscriptionDaysRemaining: previous.subscriptionDaysRemaining ?? null
      },
      usersByRole: raw.usersByRole || {},
      activityTrends: raw.activityTrends || [],
      userEngagement: raw.userEngagement || [],
      recentActivities: all.filter(inPeriod),
      companyInfo: raw.companyInfo || {},
      // Newer backend fields — every tab falls back gracefully when absent.
      quota: raw.quota || raw.quotas || null,
      mfaCoverage: raw.mfaCoverage || null,
      mfaByRole: raw.mfaByRole || null,
      usersGrowth: raw.usersGrowth || null,
      dormantUsers: raw.dormantUsers || []
    };
  }, [raw, previous, period]);

  const tabs = [
    { id: 'overview', label: tr('tabs.overview') },
    { id: 'users', label: tr('tabs.users') },
    { id: 'activities', label: tr('tabs.activities') },
    { id: 'accounts', label: tr('tabs.accounts') }
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mx-auto" />
          <p className="mt-4 text-sm text-slate-500">{tr('loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !view) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
          <p className="text-sm flex-1">{tr('loadError')}</p>
          <button onClick={() => window.location.reload()} className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700">
            {tr('refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard Administrateur</h1>
          {view.companyInfo.name && <p className="text-sm text-gray-500 mt-0.5">{view.companyInfo.name}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {updatedAt && (
            <span className="text-xs text-slate-500 bg-white px-3 py-2 rounded-lg border border-slate-200">
              {tr('lastUpdated')} : {updatedAt.toLocaleString()}
            </span>
          )}
          <PeriodSelector value={period} onChange={setPeriod}
            labels={{ 7: tr('period7'), 30: tr('period30'), 90: tr('period90') }} />
          <button onClick={handleRefresh} disabled={isRefreshing} title={tr('refresh')}
            className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-blue-50 bg-white border border-slate-200 transition-colors">
            <svg className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'overview' && <AdminOverview view={view} t={tr} period={period} />}
      {activeTab === 'users' && <AdminUsers view={view} t={tr} />}
      {activeTab === 'activities' && <AdminActivities view={view} t={tr} period={period} />}
      {activeTab === 'accounts' && <AdminAccounts view={view} t={tr} />}
    </div>
  );
};

export default AdminDashboard;
